/**
 * 外部扫码充值支付服务
 * 处理 PC 扫码打开 H5 页面的充值支付流程
 *
 * 流程：
 * 1. 扫码进入时，从启动参数中检测支付相关字段 (amount/a + no/user_id)
 * 2. 存储支付数据到本地缓存
 * 3. 登录后调用 rechargePayM 接口获取支付链接并跳转
 */
import { rechargePayExternal } from '@/api/wallet';
import { useTokenStore } from '@/store/modules/token';

/** 支付数据缓存 key（与 Vue2 保持一致） */
const PAY_STORAGE_KEY = 'storageKey__52_temp__payAmount';

/**
 * 获取当前运行平台类型
 * 0:未知，1：pc, 2：h5，3：安卓，4：ios, 5: 微信小程序，6：微信浏览器
 */
function getProjectPlatform(): number {
  // #ifdef APP-PLUS
  const platform = uni.getSystemInfoSync().platform;
  if (platform === 'android') return 3;
  if (platform === 'ios') return 4;
  // #endif

  // #ifdef MP-WEIXIN
  return 5;
  // #endif

  // #ifdef H5
  const ua = window.navigator.userAgent.toLowerCase();
  if (/micromessenger/i.test(ua)) return 6;
  return 2;
  // #endif

  return 0;
}

/**
 * 从启动参数中检测并保存支付数据
 * 当 URL 中包含 amount(或 a) + no(或 user_id) 时，认为是扫码支付场景
 */
export function saveRechargePayFromLaunchOptions(query: Record<string, any>) {
  if (!query) return;

  const hasAmount = !!(query.amount || query.a);
  const hasOrderNo = !!(query.no || query.user_id);

  if (hasAmount && hasOrderNo) {
    // 兼容旧参数名 a -> amount
    const payData = { ...query };
    if (payData.a && !payData.amount) {
      payData.amount = payData.a;
    }
    uni.setStorageSync(PAY_STORAGE_KEY, payData);
  }
}

/**
 * 清除本地存储的支付数据
 */
export function clearRechargePayData() {
  uni.removeStorageSync(PAY_STORAGE_KEY);
}

/**
 * 检查是否存在待处理的支付数据
 */
export function hasPendingRechargePay(): boolean {
  return !!uni.getStorageSync(PAY_STORAGE_KEY);
}

/**
 * 执行外部充值支付
 *
 * 调用时机：
 * - App.vue onLaunch 初始化完成后
 * - 登录页登录成功后
 * - pay-error 页面登录完成后
 *
 * 流程：
 * - 无支付数据 → 返回 false
 * - 未登录 → 跳转 pay-error (code=0) 显示未登录提示，用户手动登录后继续
 * - 已登录 → 调用接口获取支付链接并跳转，失败则跳转 pay-error 携带接口错误码
 *
 * @returns true 表示已处理支付（无论成功失败），false 表示无待处理支付
 */
export async function handleRechargePay(): Promise<boolean> {
  const payData = uni.getStorageSync(PAY_STORAGE_KEY);
  if (!payData) {
    return false;
  }

  const tokenStore = useTokenStore();

  // 未登录，跳转到 pay-error 显示未登录提示，用户手动点击去登录
  if (!tokenStore.hasLogin) {
    uni.navigateTo({
      url: `/pages/common/pay-error?code=0&msg=${encodeURIComponent('当前未登录，请登录后进行支付验证')}`,
    });
    return true;
  }

  // 已登录，调用支付接口
  try {
    const params = {
      ...payData,
      type: getProjectPlatform(),
    };

    const res = await rechargePayExternal(params);
    const payUrl = (res as any)?.data?.url || (res as any)?.url;

    if (payUrl) {
      // 支付成功才清空支付数据
      clearRechargePayData();

      // #ifdef H5
      window.location.href = payUrl;
      // #endif

      // #ifndef H5
      // 非 H5 环境使用 webview 打开
      uni.navigateTo({
        url: `/pages/common/webview?url=${encodeURIComponent(payUrl)}`,
      });
      // #endif
    }
    else {
      clearRechargePayData();
      uni.redirectTo({
        url: `/pages/common/pay-error?code=-1&msg=${encodeURIComponent('获取支付链接失败')}`,
      });
    }
  }
  catch (err: any) {
    const errCode = err?.code || err?.status || 0;
    const errMsg = err?.msg || err?.message || '支付异常';

    // 清空支付数据
    clearRechargePayData();

    // 跳转到支付错误页，携带接口实际错误码
    uni.redirectTo({
      url: `/pages/common/pay-error?code=${errCode}&msg=${encodeURIComponent(errMsg)}`,
    });
  }

  return true;
}
